import { useSearchParams } from 'react-router-dom';
import type { TableState } from '../utils';
import { Decoder } from './Decoder';
import { useCallback } from 'react';

const getSearchParams = () => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return urlParams.get('f');
};

const decodeSearchParams = async (): Promise<TableState | null> => {
  const filters = getSearchParams();

  if (filters) {
    return await Decoder.decode(filters);
  }

  return null;
};

export const SearchParam = {
  get: getSearchParams,
  decode: decodeSearchParams,
};

export const useSavedViewParam = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Always `replace: true` so filter/view edits do not pollute browser history
   * (Back should leave the table, not undo each keystroke).
   */
  const updateSavedViewParam = useCallback(
    async (newParams: TableState | string | null) => {
      if (!newParams) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('f');
            return next;
          },
          { replace: true },
        );
        return;
      }

      const encoded = typeof newParams === 'string' ? newParams : await Decoder.encode(newParams);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('f', encoded);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return {
    savedViewParam: searchParams.get('f') ?? undefined,
    updateSavedViewParam,
  };
};
